import { useCallback, useEffect, useState } from "react";
import { Search, Play, Pause, Clock, Calendar, Film, Music, Loader2, X } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useAudio } from "@/context/AudioContext";
import { RADIO } from "@/constants/testIds";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const fmtDuration = (s) => {
  if (!s) return "";
  const m = Math.floor(s / 60);
  return `${m}min ${(s % 60).toString().padStart(2, "0")}s`;
};
const fmtDate = (d) => {
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString("pt-BR");
  } catch {
    return d;
  }
};
const PAGE = 12;

const RadioLibrary = () => {
  const [episodes, setEpisodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [programs, setPrograms] = useState([]);
  const [q, setQ] = useState("");
  const [program, setProgram] = useState("all");
  const [kind, setKind] = useState("all");
  const [loading, setLoading] = useState(true);
  const [video, setVideo] = useState(null);
  const { playTrack, toggle, current, isPlaying } = useAudio();

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  const fetchPage = useCallback(async (p, replace) => {
    setLoading(true);
    const params = { page: p, limit: PAGE };
    if (program !== "all") params.program = program;
    if (kind !== "all") params.kind = kind;
    if (q.trim()) params.q = q.trim();
    try {
      const { data } = await api.get("/episodes", { params });
      setEpisodes((prev) => (replace ? data.items : [...prev, ...data.items]));
      setTotal(data.total);
      setPage(p);
      if (replace) api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
    } catch {}
    setLoading(false);
  }, [program, kind, q]);

  useEffect(() => {
    const t = setTimeout(() => fetchPage(1, true), q ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchPage, q]);

  const onPlay = (ep) => {
    if (ep.kind === "video") {
      setVideo(ep);
      return;
    }
    const src = mediaUrl(ep.media_url);
    if (current?.src === src) {
      toggle();
      return;
    }
    playTrack({ title: ep.title, subtitle: ep.program, cover: mediaUrl(ep.cover_url) || "/icon-512.png", src, isLive: false });
  };

  const chips = [{ id: "all", name: "Todos" }, ...programs];

  return (
    <section className="max-w-6xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
      <div className="mb-2 text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">Rádio · Biblioteca</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-[var(--fi-blue)] leading-[0.9] mb-6">Episódios anteriores</h1>

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input data-testid={RADIO.librarySearch} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar episódios..." className="w-full pl-11 pr-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-[var(--fi-blue)] outline-none text-sm" />
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {[["all", "Áudio e vídeo"], ["audio", "Áudio"], ["video", "Vídeo"]].map(([id, label]) => (
          <button key={id} data-testid={`library-kind-${id}`} onClick={() => setKind(id)} className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold border transition-colors ${kind === id ? "bg-[var(--fi-red)] border-[var(--fi-red)] text-white" : "bg-white border-gray-200 text-gray-600 hover:border-[var(--fi-blue)]"}`}>{label}</button>
        ))}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4" style={{ scrollbarWidth: "none" }}>
        {chips.map((c) => (
          <button key={c.id} data-testid={RADIO.filterChip(c.id)} onClick={() => setProgram(c.id)} className={`shrink-0 px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold border transition-colors ${program === c.id ? "bg-[var(--fi-blue)] border-[var(--fi-blue)] text-white" : "bg-white border-gray-200 text-gray-600 hover:border-[var(--fi-blue)]"}`}>{c.name}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="library-grid">
        {episodes.map((ep) => {
          const src = mediaUrl(ep.media_url);
          const playing = ep.kind === "audio" && current?.src === src && isPlaying;
          return (
            <article key={ep.id} data-testid={RADIO.libraryCard(ep.id)} className="rounded-xl bg-white shadow-[0_6px_24px_rgba(13,27,75,0.06)] overflow-hidden flex hover:shadow-[0_12px_36px_rgba(13,27,75,0.12)] transition-shadow">
              <button type="button" onClick={() => onPlay(ep)} className="relative w-28 h-28 shrink-0 bg-[var(--fi-blue)]/5 text-[var(--fi-blue)] flex items-center justify-center" aria-label={ep.kind === "video" ? "Assistir" : "Ouvir"}>
                {ep.cover_url ? <img src={mediaUrl(ep.cover_url)} alt="" loading="lazy" className="w-full h-full object-cover" /> : ep.kind === "video" ? <Film className="w-8 h-8" /> : <Music className="w-8 h-8" />}
                {ep.kind === "video" && <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-bold uppercase tracking-widest">Vídeo</span>}
              </button>
              <div className="flex-1 min-w-0 p-4 flex flex-col">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--fi-red)]">{ep.program}</div>
                <div className="font-display text-lg font-extrabold text-[var(--fi-blue)] leading-tight line-clamp-2 mt-0.5">{ep.title}</div>
                <div className="mt-auto flex items-center justify-between pt-3">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(ep.date)}</span>
                    {ep.duration_seconds > 0 && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDuration(ep.duration_seconds)}</span>}
                  </div>
                  <button data-testid={RADIO.libraryPlay(ep.id)} onClick={() => onPlay(ep)} className="w-10 h-10 rounded-full bg-[var(--fi-red)] text-white flex items-center justify-center hover:scale-105 active:scale-95 transition-transform" aria-label={playing ? `Pausar ${ep.title}` : `Tocar ${ep.title}`}>
                    {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {loading && <div className="flex justify-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {!loading && episodes.length === 0 && (
        <div className="text-center py-16 text-gray-400 font-medium" data-testid="library-empty">Nenhum episódio publicado ainda.</div>
      )}
      {!loading && episodes.length < total && (
        <div className="flex justify-center mt-6">
          <button data-testid="library-load-more" onClick={() => fetchPage(page + 1, false)} className="px-6 py-3 rounded-xl bg-white border border-gray-200 hover:border-[var(--fi-blue)] text-[var(--fi-blue)] text-xs font-bold uppercase tracking-widest">Carregar mais ({total - episodes.length})</button>
        </div>
      )}

      <Dialog open={!!video} onOpenChange={(o) => !o && setVideo(null)}>
        <DialogContent className="p-0 bg-black border-0 max-w-4xl overflow-hidden [&>button]:text-white" data-testid="video-dialog">
          <DialogTitle className="sr-only">{video?.title}</DialogTitle>
          {video && (
            <div>
              <video key={video.id} src={mediaUrl(video.media_url)} poster={mediaUrl(video.cover_url) || undefined} controls autoPlay playsInline preload="metadata" className="w-full max-h-[70vh] bg-black" data-testid="video-player" />
              <div className="p-4 text-white">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--fi-red)]">{video.program} · {fmtDate(video.date)}</div>
                <div className="font-display text-xl font-extrabold leading-tight mt-0.5">{video.title}</div>
                {video.description && <p className="text-white/70 text-sm mt-2 whitespace-pre-line">{video.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default RadioLibrary;
